import { RefObject, useCallback, useMemo } from 'react'
import { Flex, Box } from 'theme-ui'
import { Formik, Form, FormikProps } from 'formik'
import { lazy, object } from 'yup'
import { Paper, Heading, Text, Button, Loading } from 'components'
import {
  StudentFormSchema,
  UserTypeValidation,
  TeachingOrganizationValidation,
  NetsuiteValidation,
  NonPublicCountrySchema,
  LocationValidation,
  TimeValidation,
  ContactOptionValidation,
  TeachingOrganizationIdValidation,
} from 'components/Form'
import { AccountActions } from './'
import {
  ConfigUtils,
  StatusType,
  ViewMode,
  StudentUpdateFormValues,
  StudentData,
  StudentUpdateRequest,
  TeacherData,
  UserUpdateFormValues,
} from 'types'
import { notEditableFields } from 'utils/permissions'
import { getTMLevel } from 'components/Form/Levels'
import { STUDENT_DC, STUDENT_INPERSON, STUDENT_UPDATE } from '../../constants'
import { TeacherInfoSection } from '../FormSections/TeacherInfoSection'
import { useDelayedAction } from 'hooks/useDelayedAction'
import {
  BasicUserSection,
  InstructionDetailsSection,
  OrganizationDetailsSection,
  StatusSection,
} from '../FormSections'
import { getChangedValues } from 'utils/helpers'

export type UpdateStudentFormProps = {
  user: StudentData
  teacher?: TeacherData
  disabled?: boolean
  isSupport?: boolean
  loading?: boolean
  reading?: boolean
  responseError?: string
  configUtils: ConfigUtils
  viewMode: ViewMode
  formikRef: RefObject<FormikProps<UserUpdateFormValues>>
  isUserUsingNewOrgs: boolean
  onEnterEditing: () => void
  onLeaveForm: () => void
  onSubmitForm: (values: StudentUpdateRequest, fullname?: string) => void
  onCancelEditing: () => void
  setTeacherId: (id: string) => void
  access?: {
    viewDropdownDate: boolean
  }
}

type UserTypeProps = 'CustomerSupportAgent' | 'Teacher'
export type FieldNameProps = keyof UserUpdateFormValues

export function getUpdateValues({
  initialValues,
  values,
}: {
  initialValues: UserUpdateFormValues | any
  values: UserUpdateFormValues | any
}): Partial<UserUpdateFormValues> {
  // we never send these with updates
  const NEVER_UPDATE_KEYS = [
    'status',
    '__teachingOrg',
    '__teachingOrgId',
    '__level',
    '__country',
    '__defaultTeachingOrg',
    '__defaultTeachingOrgId',
  ]
  const updatedValues = getChangedValues<UserUpdateFormValues>({
    initialValues,
    values,
  })
  NEVER_UPDATE_KEYS.forEach((key) => {
    delete updatedValues[key as keyof UserUpdateFormValues]
  })
  return updatedValues
}

export function hasChanged({
  initialValues,
  values,
}: {
  initialValues: UserUpdateFormValues
  values: UserUpdateFormValues
}): boolean {
  const changed = getUpdateValues({ initialValues, values })
  return !!Object.keys(changed).length
}

const unlinkNetsuitData = (user: StudentUpdateFormValues) => {
  user.netsuiteEmail = undefined
  user.netsuiteForename = undefined
  user.netsuiteSurname = undefined
  user.netsuiteId = undefined
  return user
}

const isDisabledField = (
  fieldName: FieldNameProps,
  status: StatusType,
  isSupport: boolean
): boolean => {
  const userType: UserTypeProps = isSupport ? 'CustomerSupportAgent' : 'Teacher'
  if (notEditableFields[userType][status][0] === 'all') {
    return true
  }
  return notEditableFields[userType][status].includes(fieldName)
}

export function StudentForm({
  responseError,
  isSupport = false,
  user,
  teacher,
  configUtils,
  disabled = false,
  loading = false,
  formikRef,
  viewMode,
  isUserUsingNewOrgs,
  onEnterEditing,
  onSubmitForm,
  onCancelEditing,
  onLeaveForm,
  setTeacherId,
  access,
}: UpdateStudentFormProps) {
  const delayed = useDelayedAction()

  const initialValues = useMemo(
    () =>
      ({
        ...user,
        userType: user.meditatorUserType ?? user.userType,
        phone: user.phone ? user.phone : '',
        teacher: user.teacherID,
        teachingOrgId: user.teachingOrgId ? user.teachingOrgId : user.orgId,
        locationId: user.locationId ? user.locationId : '',
        startTime: user.startTime ? user.startTime : '',
        endTime: user.endTime ? user.endTime : '',
        appSignupDate: user.appSignupDate ? user.appSignupDate : '',
        __level: getTMLevel(user.level),
      } as UserUpdateFormValues),
    [user]
  )

  const submitUpdate = useCallback(
    (values: UserUpdateFormValues) => {
      const { id } = values
      // Update user
      const updatedValues = getUpdateValues({
        initialValues,
        values,
      })

      if (Object.keys(updatedValues).length) {
        const updateRequest = {
          ...updatedValues,
          id,
        } as StudentUpdateFormValues

        if (updateRequest.device && values.teacherID) {
          //teacherID always has to be sent if updating the device
          updateRequest.teacherID = values.teacherID
        }
        if (!isUserUsingNewOrgs) {
          if (
            !configUtils.getTeachingOrgs(
              updateRequest.countryCode ?? values.countryCode
            ).length
          ) {
            //if no teaching orgs exist for the latest country, remove the teaching Org
            updateRequest.teachingOrg = undefined
          }
          if (
            (updateRequest.netsuiteId ?? values.netsuiteId) &&
            !configUtils.canLinkToNetSuite(
              updateRequest.countryCode ?? values.countryCode,
              updateRequest.teachingOrg ?? values.teachingOrg
            )
          ) {
            //if no netsuite can be linked for the latest country and teaching org, remove the netsuite data
            unlinkNetsuitData(updateRequest)
          }
        } else {
          if (
            (updateRequest.netsuiteId ?? values.netsuiteId) &&
            !configUtils.canLinkToNetSuite(
              updateRequest.countryCode ?? values.countryCode,
              updateRequest.teachingOrgId ?? values.teachingOrgId
            )
          ) {
            //if no netsuite can be linked for the latest country and teaching org, remove the netsuite data
            unlinkNetsuitData(updateRequest)
          }
        }

        onSubmitForm(updateRequest)
      }
    },
    [onSubmitForm, initialValues, configUtils, isUserUsingNewOrgs]
  )

  const isEditable = useCallback(
    (fieldName: FieldNameProps, status: StatusType) => {
      if (disabled) {
        return false
      } else if (viewMode === 'edit') {
        return !isDisabledField(fieldName, status, isSupport)
      } else if (viewMode === 'view') {
        return false
      }
    },
    [isSupport, disabled, viewMode]
  )
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values: UserUpdateFormValues, { setSubmitting }) => {
        if (viewMode === 'edit') {
          submitUpdate(values)
        } else {
          onEnterEditing()
          setSubmitting(false)
        }
      }}
      validationSchema={() => {
        return lazy((values: UserUpdateFormValues) => {
          if (viewMode === 'view') return object({})
          let schema = StudentFormSchema
          if (
            values.userType === STUDENT_DC ||
            values.userType === STUDENT_INPERSON ||
            (values.countryCode &&
              !configUtils.isCountryPublic(values.countryCode))
          ) {
            schema = schema
              .concat(LocationValidation)
              .concat(TimeValidation)
              .concat(ContactOptionValidation)
          }
          if (isUserUsingNewOrgs)
            schema = schema.concat(TeachingOrganizationIdValidation)
          if (
            configUtils.canLinkToNetSuite(
              values.countryCode,
              isUserUsingNewOrgs ? values.teachingOrgId : values.teachingOrg
            )
          ) {
            schema = schema.concat(NetsuiteValidation)
          }
          if (configUtils.isCountryPublic(values.countryCode)) {
            schema = schema.concat(UserTypeValidation)
            if (!isUserUsingNewOrgs && values.countryCode === 'US') {
              schema = schema.concat(TeachingOrganizationValidation)
            }
          } else {
            schema = schema.concat(NonPublicCountrySchema)
          }
          return schema
        })
      }}
    >
      {({
        values,
        isSubmitting,
        handleSubmit,
        setFieldValue,
      }: FormikProps<UserUpdateFormValues>) => {
        const handleChangeTeacher = (teacher: TeacherData) => {
          setFieldValue('teacher', teacher.id)
          setTeacherId(teacher.id)
          //wait to reset the contactOption value until after the new teacherID has been set
          delayed(() => setFieldValue('contactOption', null, false))
        }

        return (
          <Form onSubmit={handleSubmit} autoComplete="off" autoCorrect="off">
            <Flex sx={{ minHeight: 130 }} variant={`forms.section.${viewMode}`}>
              <StatusSection
                isSupport={isSupport}
                viewMode={viewMode}
                isDisabledField={(field) =>
                  !isEditable(field as FieldNameProps, values.status)
                }
                configUtils={configUtils}
                startTabIndex={1}
              />
              {isSupport && teacher?.forename ? (
                <TeacherInfoSection
                  teacher={teacher}
                  changeTeacher={handleChangeTeacher}
                  viewMode={viewMode}
                />
              ) : (
                <Box sx={{ minHeight: 93 }} />
              )}
            </Flex>

            <Box variant={`forms.section.header`}>
              <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                Student information
              </Heading>
            </Box>
            <Flex variant={`forms.section.${viewMode}`}>
              <OrganizationDetailsSection
                isUserUsingNewOrgs={isUserUsingNewOrgs}
                pageContext={STUDENT_UPDATE}
                viewMode={viewMode}
                isDisabledField={(field) =>
                  !isEditable(field as FieldNameProps, values.status)
                }
                configUtils={configUtils}
                startTabIndex={11}
              />
              <BasicUserSection
                isSupport={isSupport}
                viewMode={viewMode}
                isDisabledField={(field) => !isEditable(field, values.status)}
                configUtils={configUtils}
                pageContext={STUDENT_UPDATE}
                startTabIndex={21}
              />
            </Flex>

            <Box variant={`forms.section.header`}>
              <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                Instruction details
              </Heading>
            </Box>
            <InstructionDetailsSection
              isUserUsingNewOrgs={isUserUsingNewOrgs}
              isSupport={isSupport}
              viewMode={viewMode}
              isDisabledField={(field) => !isEditable(field, values.status)}
              configUtils={configUtils}
              teacher={teacher}
              startTabIndex={31}
              showDropDownPicker={access?.viewDropdownDate === true}
            />

            {values.id && (
              <>
                <Box variant={`forms.section.header`}>
                  <Heading variant="forms.h2" sx={{ display: 'inline-block' }}>
                    Account Actions
                  </Heading>
                </Box>
                <AccountActions
                  userId={values.id}
                  device={values.device}
                  disabled={disabled}
                  userType={values.userType}
                  studentStatus={values.status}
                  dcProgress={values.dcProgress}
                  dcVersion={values.dcVersion}
                  isCustomerSupportUser={isSupport}
                  viewMode={viewMode}
                />
              </>
            )}

            <Box sx={{ mt: 8 }}>
              {responseError && (
                <Paper sx={{ boxShadow: '0 0 0 2px #fbebef' }}>
                  <Flex
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text sx={{ fontWeight: 'heading', color: '#f18aa3' }}>
                      {responseError}
                    </Text>
                  </Flex>
                </Paper>
              )}
            </Box>
            <Flex variant="forms.buttons.container" sx={{ mt: 45, mb: 165 }}>
              <Button
                variant="forms.buttons.primary"
                type="submit"
                data-cy="submit"
                disabled={
                  loading ||
                  isSubmitting ||
                  disabled ||
                  (viewMode === 'edit' &&
                    !hasChanged({ values, initialValues }))
                }
              >
                {loading ? (
                  <Loading />
                ) : viewMode === 'view' ? (
                  <span>Edit</span>
                ) : (
                  <span>Save</span>
                )}
              </Button>

              <Button
                variant="forms.buttons.secondary"
                data-cy="cancel"
                type="button"
                onClick={viewMode === 'view' ? onLeaveForm : onCancelEditing}
              >
                {viewMode === 'view' ? 'Back' : 'Cancel'}
              </Button>
            </Flex>
          </Form>
        )
      }}
    </Formik>
  )
}
